import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, ExternalLink, Loader2, Mail, Key, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export const SystemEmailSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  // SendGrid settings
  const [sendgridEnabled, setSendgridEnabled] = useState(false);
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  
  // Resend settings (system emails)
  const [resendApiKey, setResendApiKey] = useState('');
  
  // Store settings ID for proper upsert
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [domain, setDomain] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('system_email_settings')
        .select('*')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Store settings ID for proper upsert
        setSettingsId(data.id);
        
        // SendGrid settings
        setSendgridEnabled(data.sendgrid_enabled || false);
        setSendgridApiKey(data.sendgrid_api_key ? '••••••••••••' : '');
        
        // Resend settings
        setResendApiKey(data.resend_api_key ? '••••••••••••' : '');
        setDomain(data.sending_domain || '');
        setFromName(data.from_name || '');
        setFromEmail(data.from_email || '');
        setReplyToEmail(data.reply_to_email || '');
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load email settings',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSendGrid = async () => {
    try {
      setIsSaving(true);

      if (!settingsId) {
        toast({
          title: 'Settings not loaded',
          description: 'Please wait for system email settings to load before saving.',
          variant: 'destructive',
        });
        setIsSaving(false);
        return;
      }

      // Encrypt the API key if it's not a placeholder
      let encryptedApiKey = sendgridApiKey;
      if (sendgridApiKey && sendgridApiKey !== '••••••••••••') {
        const { data: encrypted, error: encryptError } = await supabase
          .rpc('encrypt_api_key', { api_key: sendgridApiKey });

        if (encryptError) throw encryptError;
        encryptedApiKey = encrypted;
      } else {
        encryptedApiKey = '';
      }

      const updateData: any = {
        sendgrid_enabled: sendgridEnabled,
        is_active: sendgridEnabled,
      };

      // Only update API key if it was changed
      if (encryptedApiKey && encryptedApiKey !== '••••••••••••') {
        updateData.sendgrid_api_key = encryptedApiKey;
      }

      const { error } = await supabase
        .from('system_email_settings')
        .update(updateData)
        .eq('id', settingsId);

      if (error) throw error;

      toast({
        title: 'Settings saved',
        description: 'SendGrid settings have been updated successfully',
      });

      await loadSettings();
    } catch (error: any) {
      console.error('Error saving SendGrid settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save SendGrid settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveResend = async () => {
    try {
      setIsSaving(true);

      // Encrypt the API key if it's not a placeholder
      let encryptedApiKey = resendApiKey;
      if (resendApiKey && resendApiKey !== '••••••••••••') {
        const { data: encrypted, error: encryptError } = await supabase
          .rpc('encrypt_api_key', { api_key: resendApiKey });

        if (encryptError) throw encryptError;
        encryptedApiKey = encrypted;
      } else {
        encryptedApiKey = '';
      }

      const updateData: any = {
        sending_domain: domain,
        from_name: fromName,
        from_email: fromEmail,
        reply_to_email: replyToEmail || null,
        is_active: true,
      };

      if (encryptedApiKey && encryptedApiKey !== '••••••••••••') {
        updateData.resend_api_key = encryptedApiKey;
      }

      const { error } = await supabase
        .from('system_email_settings')
        .update(updateData)
        .eq('id', settingsId);

      if (error) throw error;

      toast({
        title: 'Settings saved',
        description: 'Resend settings have been updated successfully',
      });

      await loadSettings();
    } catch (error: any) {
      console.error('Error saving Resend settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save Resend settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: 'Error',
        description: 'Please enter a test email address',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsTesting(true);
      const { data, error } = await supabase.functions.invoke('test-user-email', {
        body: { testEmail },
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: 'Test email sent',
          description: 'Check your inbox for the test email',
        });
      } else {
        throw new Error(data?.error || 'Failed to send test email');
      }
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test email',
        variant: 'destructive',
      });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">System Email Settings</h2>
        <p className="text-muted-foreground">
          Configure system emails (Resend) and lead email capabilities (SendGrid)
        </p>
      </div>

      <Tabs defaultValue="resend" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="resend">System Emails (Resend)</TabsTrigger>
          <TabsTrigger value="sendgrid">Lead Emails (SendGrid)</TabsTrigger>
        </TabsList>

        <TabsContent value="sendgrid" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <CardTitle>SendGrid Configuration</CardTitle>
              </div>
              <CardDescription>
                Enable personalized lead emails. Users can send emails from their verified work email addresses.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-2">How it works:</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Users add their work email in their profile</li>
                    <li>System automatically creates SendGrid sender identity</li>
                    <li>User verifies their email via SendGrid</li>
                    <li>Once verified, user can send emails using their work email</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="sendgrid-enabled">Enable SendGrid</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow all users to send emails via SendGrid
                  </p>
                </div>
                <Switch
                  id="sendgrid-enabled"
                  checked={sendgridEnabled}
                  onCheckedChange={setSendgridEnabled}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sendgrid-api-key">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    SendGrid API Key
                  </div>
                </Label>
                <Input
                  id="sendgrid-api-key"
                  type="password"
                  placeholder="SG.xxxxxxxxxxxxxxxx"
                  value={sendgridApiKey}
                  onChange={(e) => setSendgridApiKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Get your API key from{' '}
                  <a
                    href="https://app.sendgrid.com/settings/api_keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    SendGrid Settings <ExternalLink className="inline h-3 w-3" />
                  </a>
                </p>
              </div>

              <Button onClick={handleSaveSendGrid} disabled={isSaving || !settingsId}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save SendGrid Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resend" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <CardTitle>System Email Configuration (Resend)</CardTitle>
              </div>
              <CardDescription>
                Configure Resend for authentication, notifications, and all system emails
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-2">Setup Requirements:</p>
                  <ol className="list-decimal list-inside text-sm space-y-1">
                    <li>Get your API key from{' '}
                      <a 
                        href="https://resend.com/api-keys" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium underline inline-flex items-center gap-1"
                      >
                        resend.com/api-keys <ExternalLink className="inline h-3 w-3" />
                      </a>
                    </li>
                    <li>Verify your domain at{' '}
                      <a 
                        href="https://resend.com/domains" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium underline inline-flex items-center gap-1"
                      >
                        resend.com/domains <ExternalLink className="inline h-3 w-3" />
                      </a>
                    </li>
                    <li>Ensure from_email domain matches sending_domain</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="resend-api-key">Resend API Key</Label>
                <Input
                  id="resend-api-key"
                  type="password"
                  placeholder="re_xxxxxxxxxxxxxxxx"
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="domain">Domain</Label>
                <Input
                  id="domain"
                  placeholder="yourdomain.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="from-name">From Name</Label>
                <Input
                  id="from-name"
                  placeholder="Your Company"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="from-email">From Email</Label>
                <Input
                  id="from-email"
                  type="email"
                  placeholder="noreply@yourdomain.com"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reply-to">Reply-To Email</Label>
                <Input
                  id="reply-to"
                  type="email"
                  placeholder="support@yourdomain.com"
                  value={replyToEmail}
                  onChange={(e) => setReplyToEmail(e.target.value)}
                />
              </div>

              <Button onClick={handleSaveResend} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Resend Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            <CardTitle>Test Email Configuration</CardTitle>
          </div>
          <CardDescription>
            Send a test email to verify your settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-email">Test Email Address</Label>
            <Input
              id="test-email"
              type="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </div>

          <Button onClick={handleTestEmail} disabled={isTesting}>
            {isTesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Test Email
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
